import { Suspense, lazy, useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useAppStore } from "@/store/useAppStore";
import { useThemeEffect } from "@/hooks/useThemeEffect";
import { Shell } from "@/components/Shell";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Toaster } from "@/ui/Toast";
import { Skeleton } from "@/ui/Skeleton";

// Routes are split so the first paint only pays for the landing screen.
const TodayView = lazy(() =>
  import("@/views/TodayView").then((m) => ({ default: m.TodayView })),
);
const CalendarView = lazy(() =>
  import("@/views/CalendarView").then((m) => ({ default: m.CalendarView })),
);
const TasksView = lazy(() =>
  import("@/views/TasksView").then((m) => ({ default: m.TasksView })),
);
const HabitsView = lazy(() =>
  import("@/views/HabitsView").then((m) => ({ default: m.HabitsView })),
);
const NotesView = lazy(() =>
  import("@/views/NotesView").then((m) => ({ default: m.NotesView })),
);
const SettingsView = lazy(() =>
  import("@/views/SettingsView").then((m) => ({ default: m.SettingsView })),
);
const AuthView = lazy(() =>
  import("@/views/AuthView").then((m) => ({ default: m.AuthView })),
);

function PageSkeleton() {
  return (
    <div className="mx-auto max-w-[860px] animate-fade-in px-4 py-6 sm:px-8 sm:py-10">
      <Skeleton className="h-3 w-40" />
      <Skeleton className="mt-3 h-8 w-56" />
      <Skeleton className="mt-6 h-11 w-full rounded-xl" />
      <div className="mt-8 grid gap-6 md:grid-cols-2">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="rounded-lg border border-line p-3">
            <Skeleton className="mb-3 h-3 w-24" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-4/5" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Keeps the shell (sidebar, bottom nav) mounted while a page chunk loads. */
function Page({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageSkeleton />}>{children}</Suspense>;
}

function Booting() {
  return (
    <div className="flex h-full items-center justify-center bg-shell">
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-line border-t-accent" />
        <p className="text-[13px] text-ink-faint">Đang tải dữ liệu…</p>
      </div>
    </div>
  );
}

export default function App() {
  const status = useAppStore((s) => s.status);
  const init = useAppStore((s) => s.init);

  useThemeEffect();

  useEffect(() => {
    void init();
  }, [init]);

  if (status === "loading") return <Booting />;

  if (status === "signed-out") {
    return (
      <ErrorBoundary>
        <Suspense fallback={<Booting />}>
          <Routes>
            <Route path="*" element={<AuthView />} />
          </Routes>
        </Suspense>
        <Toaster />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <Routes>
        <Route element={<Shell />}>
          <Route path="/" element={<Page><TodayView /></Page>} />
          <Route path="/calendar" element={<Page><CalendarView /></Page>} />
          <Route path="/tasks" element={<Page><TasksView /></Page>} />
          <Route path="/habits" element={<Page><HabitsView /></Page>} />
          <Route path="/notes" element={<Page><NotesView /></Page>} />
          <Route path="/notes/:noteId" element={<Page><NotesView /></Page>} />
          <Route path="/settings" element={<Page><SettingsView /></Page>} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster />
    </ErrorBoundary>
  );
}
